import { useConfigStore } from '../stores/config'

type Crop = {
  x1?: number | string,
  y1?: number | string,
  x2?: number | string,
  y2?: number | string
}

export type ImageObject = {
  imageId?: string,
  seoName?: string,
  mimetype?: string,
  width?: number,
  height?: number,
  crop?: Crop,
  croppings?: Record<string, Crop>,
  imageAltText?: string,
  imageCredit?: string
}

type SrcSetDefinition = { format?: string }

type ImgSrcParams = {
  image: ImageObject,
  brand: string,
  setName?: string,
  type?: string,
  width?: number,
  srcSetDefinition?: SrcSetDefinition
}

type VideoThumbnailSrcParams = {
  image: ImageObject,
  brand: string,
  setName?: string,
  type?: string,
  width?: number
}

export function useImgSrcSet () {
  const configStore = useConfigStore()

  function isEnabled (brand: string | null | undefined): boolean | null {
    if (!brand) return null
    return configStore.rsConfig.imageSrcDefinitions
      ? configStore.rsConfig.imageSrcDefinitions.isEnabled
      : true
  }

  function getCropping (
    obj: ImageObject | null | undefined,
    srcSetDefinition?: SrcSetDefinition
  ): Crop | undefined {
    if (!obj) return undefined
    if (obj.crop) return obj.crop
    if (obj.croppings && srcSetDefinition?.format) {
      return obj.croppings[srcSetDefinition.format]
    }
  }

  function getCroppedImgSize (image: ImageObject | null | undefined): { width?: number, height?: number } {
    if (!image) return {}
    if (!image.crop && image.width && image.height) return { width: image.width, height: image.height }
    if (image.crop) {
      return {
        width: Math.abs(Math.floor(image.width! / 100 * (Number(image.crop.x2) - Number(image.crop.x1)))),
        height: Math.abs(Math.floor(image.height! / 100 * (Number(image.crop.y2) - Number(image.crop.y1))))
      }
    }
    return {}
  }

  function getImageExtension (mimetype: string | undefined, expectedType: string | undefined): string {
    if (mimetype === 'image/gif' && expectedType !== 'webp') return '.gif'
    if (expectedType && expectedType.length > 0) return `.${expectedType}`
    return '.jpeg'
  }

  function getImgDefinitions (brand: string, setName?: string) {
    if (!configStore.rsConfig.imageSrcDefinitions) {
      console.error(`no image src definitions for brand '${brand}'`)
      return undefined
    }
    const defs = configStore.rsConfig.imageSrcDefinitions.definitions
    if (setName) {
      if (defs[setName]) return defs[setName]
      console.log(`[WARN] there is no imageSrcDefinition set with the name '${setName}' - falling back to default`)
    }
    return defs.defaults
  }

  function createCroppingUrlParams (crop: Crop | undefined): string {
    if (crop && crop.x1 && crop.y1 && crop.x2 && crop.y2) {
      return ',ca=' + crop.x1 + ',' + crop.y1 + ',' + crop.x2 + ',' + crop.y2
    }
    return ''
  }

  function createImageUrl (
    image: ImageObject,
    brand: string,
    width?: number,
    cropping?: Crop,
    type?: string
  ): string {
    const imageServiceUrl = configStore.rsConfig.imageServiceUrl + '/'
    return imageServiceUrl +
      (image?.seoName && image.seoName.length > 0 ? image.seoName : 'image') +
      ',id=' + image?.imageId +
      // workaround until whitelabel gets branded and provides data on gui, cs, etc
      ',b=' + (brand === 'whitelabel' ? 'maennersache' : brand) +
      ',w=' + width +
      createCroppingUrlParams(cropping) +
      ',rm=sk' + // rm=resizeMode, sk=Only Shrink Larger Images
      getImageExtension(image?.mimetype, type)
  }

  function resolutionToFormat (width: number, height: number): 'landscape' | 'portrait' | 'square' {
    if (width > height) return 'landscape'
    if (width < height) return 'portrait'
    return 'square'
  }

  function getImageFormat (image: ImageObject | null | undefined): 'landscape' | 'portrait' | 'square' | undefined {
    if (!image?.width || !image?.height) return undefined
    if (image.crop &&
      image.crop.x1 !== undefined &&
      image.crop.y1 !== undefined &&
      image.crop.x2 !== undefined &&
      image.crop.y2 !== undefined) {
      const w = (image.width * parseFloat(String(image.crop.x2))) / 100 - (image.width * parseFloat(String(image.crop.x1))) / 100
      const h = (image.height * parseFloat(String(image.crop.y2))) / 100 - (image.height * parseFloat(String(image.crop.y1))) / 100
      return resolutionToFormat(w, h)
    }
    return resolutionToFormat(image.width, image.height)
  }

  function createImgSrc ({ image, brand, setName, type, width, srcSetDefinition }: ImgSrcParams): string | null {
    if (!image?.imageId || !brand) return null
    return createImageUrl(image, brand, width ?? getImgDefinitions(brand, setName)?.maxWidth, getCropping(image, srcSetDefinition), type)
  }

  function createImgSrcSet (image: ImageObject, brand: string, setName?: string, type?: string): string {
    if (!isEnabled(brand)) return ''
    const definitions = getImgDefinitions(brand, setName)
    if (!definitions?.srcSet) return ''
    return definitions.srcSet
      .map((s) =>
        createImageUrl(image, brand, Number(s.width), getCropping(image, s), type) + ' ' + s.width + 'w'
      )
      .join(', ')
  }

  function createImgSrcSetArray (image: ImageObject, brand: string, setName?: string, type?: string): { src: string, viewportWidth: string }[] {
    if (!isEnabled(brand)) return []
    const definitions = getImgDefinitions(brand, setName)
    if (!definitions?.srcSet) return []
    return definitions.srcSet.map((s) => ({
      src: createImageUrl(image, brand, Number(s.width), getCropping(image, s), type),
      viewportWidth: s.viewportWidth
    }))
  }

  function createVideoThumbnailSrc ({ image, brand, setName, type, width }: VideoThumbnailSrcParams): string | null {
    if (!image?.imageId || !brand) return null
    return configStore.rsConfig.imageServiceUrl + '/' +
      'image,id=' + image.imageId +
      ',b=' + brand +
      ',w=' + (width ?? getImgDefinitions(brand, setName)?.maxWidth) +
      ',rm=sk' +
      getImageExtension(image.mimetype, type)
  }

  function getImageAltText (image: ImageObject | null | undefined, fallbackAltText = ''): string {
    return (
      (image?.imageAltText && image.imageAltText.length > 0 ? image.imageAltText : fallbackAltText) +
      (image?.imageCredit && image.imageCredit.length > 0 ? ` - Foto: ${image.imageCredit}` : '')
    ).replace(/['"]+/g, '')
  }

  return {
    isEnabled,
    getCropping,
    getCroppedImgSize,
    getImageExtension,
    getImgDefinitions,
    createImageUrl,
    createCroppingUrlParams,
    getImageFormat,
    resolutionToFormat,
    createImgSrc,
    createImgSrcSet,
    createImgSrcSetArray,
    createVideoThumbnailSrc,
    getImageAltText
  }
}
